import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartDataset,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { isEqual } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
);

// Set global font defaults
ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

function PitLossLine() {
  const pitLosses = useSelector(
    (state: RootState) => state.virtualGaps.data.pitLosses,
    isEqual,
  );

  const pitStopLoss = useSelector(
    (state: RootState) => state.virtualGaps.data?.refs?.pitStopLoss,
  );

  const timingLoops = useSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.track?.info?.timingLoops,
    isEqual,
  );

  // Get second to last loop name
  const secondToLastLoopName = useMemo(() => {
    if (!timingLoops) return "Track Gap (s)";
    const keys = Object.keys(timingLoops);
    if (keys.length < 2) return "Track Gap (s)";
    const secondToLastKey = keys[keys.length - 2];
    const loopInfo = timingLoops[secondToLastKey as `${number}`];
    return `Gap at ${loopInfo?.name || secondToLastKey} (s)`;
  }, [timingLoops]);

  const chartData = useMemo(() => {
    if (!pitLosses || pitLosses.length === 0 || !pitStopLoss) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Use median gating range (0.9x to 1.1x of pitStopLoss)
    const minGap = pitStopLoss * 0.9;
    const maxGap = pitStopLoss * 1.1;

    // Generate gap values in 0.01s increments
    const xGaps: number[] = [];
    for (let gap = minGap; gap <= maxGap; gap += 0.01) {
      xGaps.push(Math.round(gap * 100) / 100);
    }

    // Calculate probability for each gap value
    const probability: number[] = [];
    for (const testGap of xGaps) {
      const results: boolean[] = [];

      for (const entry of pitLosses) {
        if (entry.keptPosition && testGap > entry.gap) {
          // If they kept position at entry.gap, they would also keep at testGap (which is larger)
          results.push(true);
        } else if (!entry.keptPosition && testGap < entry.gap) {
          // If they lost position at entry.gap, they would also lose at testGap (which is smaller)
          results.push(false);
        }
      }

      if (results.length > 0) {
        const keptCount = results.filter((r) => r).length;
        probability.push(Math.round((keptCount / results.length) * 100));
      } else {
        probability.push(0);
      }
    }

    const datasets: ChartDataset<"line">[] = [
      {
        label: "Probability of keeping position",
        data: probability,
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.1,
        fill: true,
      },
    ];

    return {
      labels: xGaps.map((g) => g.toFixed(2)),
      datasets,
    };
  }, [pitLosses, pitStopLoss]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Pit Stop Gap To Keep Position",
        color: "#ffffff",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
      tooltip: {
        callbacks: {
          title: () => "",
          label: (context: { label: string; raw: unknown }) => {
            return [
              `Track Gap: ${context.label}s`,
              `Keep Position: ${context.raw}%`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: secondToLastLoopName,
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          maxTicksLimit: 10,
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
      },
      y: {
        title: {
          display: true,
          text: "Ratio of keeping position (%)",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          stepSize: 10,
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
        min: 0,
        max: 100,
      },
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
  };

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default PitLossLine;
